export interface SharedRecord {
  id: string;
  files: File[];
  expiresAt: number;
  owner?: string;
}

function openShares(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('alexia-shares', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('shares', { keyPath: 'id' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** La primera sesión autenticada reclama el envío antes de mostrar nombres o archivos. */
export async function claimSharedRecord(id: string, owner: string): Promise<SharedRecord | null | undefined> {
  const db = await openShares();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction('shares', 'readwrite'); const store = tx.objectStore('shares');
      let result: SharedRecord | null | undefined;
      const request = store.get(id);
      request.onsuccess = () => {
        const record = request.result as SharedRecord | undefined;
        if (!record || record.expiresAt <= Date.now()) { if (record) store.delete(id); result = undefined; return; }
        if (record.owner && record.owner !== owner) { result = null; return; }
        result = { ...record, owner }; store.put(result);
      };
      tx.oncomplete = () => resolve(result); tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

export async function saveSharedRecord(record: SharedRecord, owner: string): Promise<void> {
  const db = await openShares();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('shares', 'readwrite'); const store = tx.objectStore('shares');
      const request = store.get(record.id);
      request.onsuccess = () => {
        const current = request.result as SharedRecord | undefined;
        if (!current || current.owner !== owner) { tx.abort(); return; }
        store.put({ ...record, owner });
      };
      tx.oncomplete = () => resolve(); tx.onabort = () => reject(new Error('Los archivos no pertenecen a esta sesión.')); tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

export async function removeSharedRecord(id: string, owner: string): Promise<void> {
  const db = await openShares();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('shares', 'readwrite'); const store = tx.objectStore('shares');
      const request = store.get(id);
      request.onsuccess = () => { if ((request.result as SharedRecord | undefined)?.owner === owner) store.delete(id); };
      tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}

/** El cierre de sesión elimina también adjuntos que todavía no se hayan procesado. */
export async function clearSharedRecords(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  const db = await openShares();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('shares', 'readwrite'); tx.objectStore('shares').clear();
      tx.oncomplete = () => resolve(); tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}
